// Boundary for provider schema 0.2. Source observations never pass through editorial mapping.
import { clone, scheduleLabel } from './localEventModel';
export function actionPermissions(item) {
  const actions = new Set(Array.isArray(item?.allowed_actions) ? item.allowed_actions : []);
  return Object.fromEntries([['save', 'save_draft'], ['validate', 'validate'], ['publish', 'publish'], ['hold', 'hold'], ['withdraw', 'withdraw'], ['delete', 'delete']].map(([view, wire]) => [view, actions.has(wire)]));
}
export function editorialFromApi(editorial) {
  const result = clone(editorial), schedule = result.schedule || {};
  result.schedule = { ...schedule, opening_or_recurrence_note: schedule.opening_hours_note ?? schedule.recurrence_note ?? null,
    occurrences: (schedule.occurrences || []).map(row => ({ ...row, venue_name: row.venue?.venue_name || '', location: {
      point: row.venue?.point || null, status: row.venue?.status || 'unresolved', scope: row.venue?.scope || null,
      evidence: row.venue?.evidence_url ? [{ url: row.venue.evidence_url, method: row.venue.method }] : [],
    } })) };
  return result;
}
export function editorialToApi(editorial) {
  const result = clone(editorial), schedule = result.schedule;
  result.schedule = {
    kind: schedule.kind, timezone: schedule.timezone, source_period: schedule.source_period || null,
    opening_hours_note: schedule.kind === 'recurring' ? schedule.opening_hours_note ?? null : schedule.opening_or_recurrence_note ?? null,
    recurrence_note: schedule.kind === 'recurring' ? schedule.opening_or_recurrence_note ?? null : schedule.recurrence_note ?? null,
    exceptions_note: schedule.exceptions_note ?? null, occurrences_complete: !!schedule.occurrences_complete,
    occurrences: schedule.occurrences.map(row => {
      if (!row.id) throw Object.assign(new Error('Occurrence ID is required'), { status: 400, unknown: false });
      const location = row.location || {}, previousVenue = row.venue || {};
      return {
        id: row.id, date: row.date ?? null, end_date_inclusive: row.end_date_inclusive ?? null,
        start_time: row.start_time ?? null, end_time: row.end_time ?? null, time_precision: row.time_precision, all_day: row.all_day ?? null,
        venue: row.venue_name || location.point ? { venue_id: previousVenue.venue_id ?? null, venue_name: row.venue_name || previousVenue.venue_name || '', address: previousVenue.address ?? null,
          point: location.point || null, status: location.status || 'unresolved', method: location.evidence?.[0]?.method || previousVenue.method || null,
          evidence_url: location.evidence?.[0]?.url || null, scope: location.scope ?? null } : null,
        status: row.status || 'scheduled', note: row.note ?? null,
      };
    }),
  };
  return result;
}
export function itemFromApi(item) {
  const source = item?.source_observation?.source, validation = item?.validation;
  if (!/^[a-f0-9]{64}$/.test(item?.source_observation_sha256 || '') || (item?.source_proposal && !/^[a-f0-9]{64}$/.test(item.source_proposal_sha256 || '')) || !Object.prototype.hasOwnProperty.call(item?.editorial || {}, 'organizer_approval') || !item?.id || typeof source?.publisher !== 'string' || typeof source.source_language !== 'string' || typeof item.source_observation.original_title !== 'string' || !item.editorial?.texts || !Array.isArray(item.editorial?.schedule?.occurrences) || !item.draft_revision || !Array.isArray(item.allowed_actions) || typeof validation?.can_publish !== 'boolean' || !Array.isArray(validation.blockers) || !Array.isArray(validation.advisories)) throw new Error('Invalid local-event detail contract');
  return { ...item, editorial: editorialFromApi(item.editorial) };
}
export function approvedAssetsFromApi(item) {
  const image = item?.validation?.effective_image || item?.effective_image;
  if (!image || !['event', 'organizer_logo'].includes(image.mode) || typeof image.asset_id !== 'string' || !image.asset_id || image.rights_status !== 'permitted' || image.freshness_status !== 'current') return [];
  const urls = [image.marker_preview_url, image.modal_preview_url];
  if (urls.some(value => { try { const url = new URL(value); return url.protocol !== 'https:' || !!url.username || !!url.password; } catch { return true; } })) return [];
  return [{ ...image, id: image.asset_id, name: image.asset_id, organizer_id: item.editorial?.organizer_id, marker_url: image.marker_preview_url, modal_url: image.modal_preview_url }];
}
export function createLocalEventsWorkspaceAdapter(api) {
  return {
    localPreview: false,
    list: () => api.list({ page: 1, page_size: 100 }),
    async detail(id) { const data = await api.detail(id); return { ...data, item: itemFromApi(data.item) }; },
    validate: (id, draftRevision, revision) => api.validate(id, draftRevision, revision),
    action(operation) { return api.action(operation.kind === 'save' ? { ...operation, input: { editorial: editorialToApi(operation.input.editorial) } } : operation); },
  };
}

export function summaryScheduleFromApi(row, language) {
  const next = row.next_schedule;
  if (typeof next === 'string') return next;
  if (next) return scheduleLabel({ kind: next.kind, timezone: next.timezone, source_period: next.period, occurrences: next.occurrence ? [next.occurrence] : [] }, language);
  return scheduleLabel(row.editorial?.schedule || row.source_observation?.schedule, language);
}
export function approvedRegistryAssets(registry, item, organizerId) {
  const candidates = (registry?.assets || []).filter(asset => asset.mode === 'event' ? asset.event_id === item?.id : asset.mode === 'organizer_logo' && asset.organizer_id === organizerId);
  return candidates.flatMap(asset => approvedAssetsFromApi({ effective_image: { ...asset, asset_id: asset.id }, editorial: { organizer_id: asset.organizer_id } }));
}
export function updateSourceResolution(editorial, item, code, patch) {
  const digest = code === 'SOURCE_CHANGE' ? item.source_proposal_sha256 : item.source_observation_sha256;
  const existing = editorial.conflict_resolutions?.find(row => row.code === code) || { code, reviewed: false, note: '', evidence_url: '' };
  const next = { ...existing, ...patch, ...(digest ? { source_sha256: digest } : {}), ...(patch.reviewed === undefined ? { reviewed: false } : {}) };
  return [...(editorial.conflict_resolutions || []).filter(row => row.code !== code), next];
}

export function imageChainFromApi(image) {
  if (!image) return null;
  const entries = [image, ...(Array.isArray(image.fallbacks) ? image.fallbacks.slice(0, 2) : [])];
  const result = [];
  for (let index = 0; index < entries.length; index += 1) {
    const entry = entries[index];
    if (entry?.mode === 'category_default') break;
    if (index > 0 && (image.mode !== 'event' || entry?.mode !== 'organizer_logo')) continue;
    const asset = approvedAssetsFromApi({ effective_image: entry })[0];
    if (asset && !result.some(row => row.id === asset.id)) result.push(asset);
  }
  return result;
}
